import dayjs from "dayjs";
import { employeeImportService } from "services";
import { checkRequireValue } from "utils/object";
import { DataItem } from "utils/xlsx";

import { EMPLOYEE_CONFIG } from "./constants";
import { ModuleType, Config } from "./types";

export function convertExcelDateToJSDate(serial: number) {
  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400;
  const dateInfo = new Date(utcValue * 1000);

  const fractionalDay = serial - Math.floor(serial) + 0.0000001;

  let totalSeconds = Math.floor(86400 * fractionalDay);

  const seconds = totalSeconds % 60;

  totalSeconds -= seconds;

  const hours = Math.floor(totalSeconds / (60 * 60));
  const minutes = Math.floor(totalSeconds / 60) % 60;

  return new Date(
    dateInfo.getFullYear(),
    dateInfo.getMonth(),
    dateInfo.getDate(),
    hours,
    minutes,
    seconds
  );
}

export const getConfigs = (type: ModuleType): Config[] => {
  switch (type) {
    case "employee":
      return EMPLOYEE_CONFIG;

    case "department":
      return [];

    default:
      return [];
  }
};

export const getService = (type: ModuleType) => {
  if (type === "employee") {
    return employeeImportService;
  }
  return employeeImportService;
};

export const getFieldKey = (type: ModuleType) => {
  if (type === "employee") {
    return "employees";
  }
  return "employees";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getDataExcel = (value: any, isHeader?: boolean): DataItem => {
  return {
    value: String(value || ""),
    dataType: "string",
    ...(isHeader && {
      style: {
        background: "B74C27",
        color: "ffffff",
      },
    }),
  };
};

export const getHeaderExcel = (config: Config[]): DataItem[] => {
  const header: DataItem[] = [];

  config.forEach((item) => {
    header.push(getDataExcel(item.text));
  });

  return header;
};

export const generateExcelTemplate = (config: Config[]): DataItem[][] => {
  const header: DataItem[] = [];
  const body: DataItem[] = [];

  config.forEach((item) => {
    header.push(getDataExcel(item.text, true));
    body.push(getDataExcel(item.defaultText));
  });

  return [header, body];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getBodyExcel = (data: any[], config: Config[]): DataItem[][] => {
  const body: DataItem[][] = [getHeaderExcel(config)];

  data.forEach((item) => {
    const row: DataItem[] = [];
    config.forEach((configItem) => {
      row.push(getDataExcel(item[configItem.field]));
    });
    body.push(row);
  });

  return body;
};

type RecordItem = Record<string, string | number | boolean | string[] | null>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getDataFromExcel = (dataExcel: any[], configs: Config[]) => {
  const result = [];
  const rowBaseConfig = Array(dataExcel?.length).fill(configs);

  for (let i = 1; i < rowBaseConfig.length; i++) {
    const currentRow: string[] = (dataExcel[i] as string[]) || ([] as string[]);
    if (currentRow?.[0]) {
      const data: RecordItem = {
        invalidList: [] as string[],
      } as RecordItem;

      (rowBaseConfig[i] as Config[])?.forEach(
        (_configs: Config, index: number) => {
          const currentConfig = _configs;
          const cell = currentRow[index];

          if (currentConfig.regex && currentConfig.type !== "date") {
            if (
              cell &&
              !currentConfig.regex.test(cell) &&
              Array.isArray(data?.invalidList)
            ) {
              data?.invalidList?.push(currentConfig.field);
            }
            data[currentConfig.field] = cell || null;
          } else {
            if (currentConfig.type === "number") {
              data[currentConfig.field] = Number(cell ?? 0) || null;
            }
            if (currentConfig.type === "text") {
              data[currentConfig.field] = String(cell ?? "") || null;
            }
            if (currentConfig.type === "boolean") {
              data[currentConfig.field] =
                String(cell || "")?.toLowerCase() === "true" || cell === "1";
            }
            if (currentConfig.type === "date") {
              if (typeof cell === "number") {
                const date = convertExcelDateToJSDate(cell);
                data[currentConfig.field] = dayjs(date).format("YYYY-MM-DD");
              } else if (!currentConfig?.regex?.test(cell)) {
                if (cell && Array.isArray(data?.invalidList)) {
                  data?.invalidList?.push(currentConfig.field);
                }
                data[currentConfig.field] = cell || null;
              }
            }
          }
          if (
            currentConfig.required &&
            checkRequireValue(data[currentConfig.field])
          ) {
            if (Array.isArray(data?.invalidList)) {
              data?.invalidList?.push(currentConfig.field);
              data[currentConfig.field] = `${currentConfig.text} is required`;
            }
          }
        }
      );
      data.id = Number(+(i || 0) - 1);
      result.push(data);
    }
  }
  return result;
};
